(ns minorodata.dependency-graph.timeline.subs
  "Execution-timeline subscriptions for the dependency graph.

  State is plain re-frame db under `[:dependency-graph-timeline <master-id>]`:
  `{:timeline {:started-at :ended-at} :subtimeline {…} :executions <raw> :logs <raw>
    :preset <keyword> :live? <bool> :error <string>}`.

  The raw history is kioku's gantt: the pipeline's transforms once, in row order,
  and per window only the runs —
  `{:transforms [master-id …] :windows [{:scheduled <iso> :runs [run …]} …]}`."
  (:require
   ["date-fns" :refer [parseISO]]
   ["date-fns-tz" :refer [toZonedTime]]
   [minorodata.dependency-graph.timeline.window :refer [run-outcome]]
   [re-frame.core :as re-frame]))


(defn- parse-execution-item [{:keys [activity-id master-id transform-id started-at finished-at status]}
                             scheduled-at timezone]
  {:execution-id   activity-id
   :execution-type "transform"
   :master-id      master-id
   :transform-id   transform-id
   :status         status
   :scheduled-at   scheduled-at
   :started-at     (some-> started-at parseISO (toZonedTime timezone))
   :finished-at    (some-> finished-at parseISO (toZonedTime timezone))})

(defn parse-executions
  "Flattens the gantt windows into one item per run, each carrying its window's
   fire-time — parsed once per window, not once per run."
  [{:keys [windows] :as history} timezone]
  (when history
    (into []
          (mapcat (fn [{:keys [scheduled runs]}]
                    (let [scheduled-at (toZonedTime (parseISO scheduled) timezone)]
                      (map #(parse-execution-item % scheduled-at timezone) runs))))
          windows)))


(re-frame/reg-sub
 :dependency-graph.timeline.subs/entry
 (fn [db [_ master-id]]
   (get-in db [:dependency-graph-timeline master-id])))


(re-frame/reg-sub
 :dependency-graph.timeline.subs/raw-executions
 (fn [[_ master-id]]
   (re-frame/subscribe [:dependency-graph.timeline.subs/entry master-id]))
 (fn [entry _]
   (:executions entry)))


(re-frame/reg-sub
 :dependency-graph.timeline.subs/raw-logs
 (fn [[_ master-id]]
   (re-frame/subscribe [:dependency-graph.timeline.subs/entry master-id]))
 (fn [entry _]
   (:logs entry)))


(re-frame/reg-sub
 :dependency-graph.timeline.subs/executions-timeline
 (fn [[_ master-id]]
   (re-frame/subscribe [:dependency-graph.timeline.subs/entry master-id]))
 (fn [entry]
   (when-let [timeline (:timeline entry)]
     {:started-at (parseISO (:started-at timeline))
      :ended-at   (parseISO (:ended-at timeline))})))


(re-frame/reg-sub
 :dependency-graph.timeline.subs/executions-subtimeline
 (fn [[_ master-id]]
   (re-frame/subscribe [:dependency-graph.timeline.subs/entry master-id]))
 (fn [entry]
   (let [timeline (:subtimeline entry)]
     (when (not-empty timeline)
       {:started-at (parseISO (:started-at timeline))
        :ended-at   (parseISO (:ended-at timeline))}))))


;; Parsing hangs off the raw-* subs, not `entry`: the history and the log land
;; separately, and the live/preset/error flags change on their own too. Through
;; `entry`, each of those re-parsed the whole history; behind a raw-* sub, an
;; unchanged response stops the chain.

;; A two-argument fn, never a bare keyword: re-frame calls a computation fn as
;; `(f inputs query-vector)`, and a keyword's 2-arity is `get` with a not-found
;; default, so it would return the query vector for an absent key.
(re-frame/reg-sub
 :dependency-graph.timeline.subs/preset
 (fn [[_ master-id]]
   (re-frame/subscribe [:dependency-graph.timeline.subs/entry master-id]))
 (fn [entry _]
   (:preset entry)))


(re-frame/reg-sub
 :dependency-graph.timeline.subs/error
 (fn [[_ master-id]]
   (re-frame/subscribe [:dependency-graph.timeline.subs/entry master-id]))
 (fn [entry [_ _ request]]
   (get-in entry [:errors request])))


(re-frame/reg-sub
 :dependency-graph.timeline.subs/live?
 (fn [[_ master-id]]
   (re-frame/subscribe [:dependency-graph.timeline.subs/entry master-id]))
 (fn [entry _]
   (boolean (:live? entry))))


(re-frame/reg-sub
 :dependency-graph.timeline.subs/transforms
 (fn [[_ master-id]]
   (re-frame/subscribe [:dependency-graph.timeline.subs/raw-executions master-id]))
 (fn [history _]
   (:transforms history)))


(re-frame/reg-sub
 :dependency-graph.timeline.subs/executions
 (fn [[_ master-id]]
   [(re-frame/subscribe [:dependency-graph.timeline.subs/raw-executions master-id])
    (re-frame/subscribe [:app.subs/timezone])])
 (fn [[executions timezone]]
   (parse-executions executions timezone)))


(defn scheduled-by-activity
  "activity-id -> the fire-time of the gantt window the activity was bucketed
   into, so a log row can name the run (gantt column) it belongs to."
  [{:keys [windows]}]
  (into {}
        (mapcat (fn [{:keys [scheduled runs]}]
                  (let [scheduled-at (parseISO scheduled)]
                    (map (fn [{:keys [activity-id]}] [activity-id scheduled-at]) runs))))
        windows))

(defn parse-logs
  "A vector, so the virtualised table can slice it."
  [logs scheduled]
  (some->> logs
           (mapv (fn [{:keys [activity-id master-id transform-id name job-id status started-at finished-at error]}]
                  {:execution-id activity-id
                   :master-id    master-id
                   :transform-id transform-id
                   :job-id       job-id
                   :name         name
                   :status       status
                   :scheduled-at (get scheduled activity-id)
                   :started-at   (parseISO started-at)
                   :finished-at  (some-> finished-at parseISO)
                   :error        error}))))


(re-frame/reg-sub
 :dependency-graph.timeline.subs/scheduled-by-activity
 (fn [[_ master-id]]
   (re-frame/subscribe [:dependency-graph.timeline.subs/raw-executions master-id]))
 (fn [executions _]
   (scheduled-by-activity executions)))


(re-frame/reg-sub
 :dependency-graph.timeline.subs/execution-logs
 (fn [[_ master-id]]
   [(re-frame/subscribe [:dependency-graph.timeline.subs/raw-logs master-id])
    (re-frame/subscribe [:dependency-graph.timeline.subs/scheduled-by-activity master-id])])
 (fn [[logs scheduled]]
   (parse-logs logs scheduled)))


;; The runs that started: zoned fire-time and outcome.
(re-frame/reg-sub
 :dependency-graph.timeline.subs/started-runs
 (fn [[_ master-id]]
   [(re-frame/subscribe [:dependency-graph.timeline.subs/raw-executions master-id])
    (re-frame/subscribe [:app.subs/timezone])])
 (fn [[history timezone]]
   (some->> (:windows history)
            (into [] (keep (fn [{:keys [scheduled runs]}]
                             (when (some :started-at runs)
                               {:at      (-> scheduled parseISO (toZonedTime timezone))
                                :outcome (run-outcome (map :status runs))})))))))


(re-frame/reg-sub
 :dependency-graph.timeline.subs/execution-schedules
 (fn [[_ master-id]]
   (re-frame/subscribe [:dependency-graph.timeline.subs/started-runs master-id]))
 (fn [runs _]
   (some->> runs (map :at))))


;; Keyed on epoch ms: CLJS maps cannot key on a Date.
(re-frame/reg-sub
 :dependency-graph.timeline.subs/execution-schedule-statuses
 (fn [[_ master-id]]
   (re-frame/subscribe [:dependency-graph.timeline.subs/started-runs master-id]))
 (fn [runs _]
   (into {} (map (juxt #(.getTime (:at %)) :outcome)) runs)))


(re-frame/reg-sub
 :dependency-graph.timeline.subs/run
 (fn [[_ master-id]]
   (re-frame/subscribe [:dependency-graph.timeline.subs/entry master-id]))
 (fn [entry _]
   (:run entry)))
